import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag } from 'lucide-react';
import { formatDate, formatReadTime } from '../../utils/blogUtils';

const BlogCard = ({ post, featured = false, blogBasePath = '/blog' }) => {
  if (!post) return null;

  const cardClasses = featured 
    ? "bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border-2 border-indigo-100 p-6"
    : "bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200 p-6";

  return (
    <article className={cardClasses}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <Link 
              to={`${blogBasePath}/${post.slug}`}
              className="group"
            >
              <h3 className={`font-bold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2 ${
                featured ? 'text-xl mb-3' : 'text-lg mb-2'
              }`}>
                {post.title}
              </h3>
            </Link>
            
            {/* Meta information */}
            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.publishDate)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{formatReadTime(post.readTime)}</span>
              </div>
            </div>
          </div>
          
          {featured && (
            <div className="ml-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Featured
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1">
          <p className={`text-gray-600 line-clamp-3 ${featured ? 'text-base' : 'text-sm'}`}>
            {post.excerpt}
          </p>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-4 mb-4">
            <div className="flex items-center flex-wrap gap-2">
              <Tag className="w-4 h-4 text-gray-400" />
              {post.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600"
                >
                  {tag}
                </span>
              ))}
              {post.tags.length > 3 && (
                <span className="text-xs text-gray-400">
                  +{post.tags.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="text-sm text-gray-500">
            By {post.author || 'PDF Statement Converter Team'}
          </div>
          
          <Link
            to={`${blogBasePath}/${post.slug}`}
            className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors"
          >
            Read more
            <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;